import React, { useState, useEffect, useMemo } from 'react';
import Styles from './ContentStrategy.module.scss';
import {
  FiCheck, FiSave, FiRefreshCw, FiTarget, FiUsers, FiMessageSquare,
  FiTrendingUp, FiCalendar, FiClock, FiZap, FiLayers, FiGrid,
  FiBarChart2, FiPieChart, FiHash, FiImage, FiVideo, FiFileText,
  FiChevronRight, FiChevronDown, FiInfo, FiStar, FiEdit3
} from 'react-icons/fi';
import { FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import contentTrainingService from 'services/contentTrainingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const PLATFORM_CONFIG = {
  twitter: {
    icon: FaTwitter,
    label: 'Twitter/X',
    color: '#1DA1F2',
    maxLength: 280,
    optimalTimes: ['9am', '12pm', '5pm'],
    bestDays: ['Tuesday', 'Wednesday', 'Thursday'],
    formats: ['thread', 'single', 'poll', 'quote'],
    cadence: '3-5 posts/day'
  },
  linkedin: {
    icon: FaLinkedin,
    label: 'LinkedIn',
    color: '#0A66C2',
    maxLength: 3000,
    optimalTimes: ['8am', '10am', '12pm'],
    bestDays: ['Tuesday', 'Wednesday', 'Thursday'],
    formats: ['article', 'carousel', 'video', 'poll'],
    cadence: '1-2 posts/day'
  },
  instagram: {
    icon: FaInstagram,
    label: 'Instagram',
    color: '#E4405F',
    maxLength: 2200,
    optimalTimes: ['11am', '1pm', '7pm'],
    bestDays: ['Monday', 'Wednesday', 'Friday'],
    formats: ['reel', 'carousel', 'story', 'single'],
    cadence: '1-2 posts/day'
  },
  youtube: {
    icon: FaYoutube,
    label: 'YouTube',
    color: '#FF0000',
    maxLength: 5000,
    optimalTimes: ['2pm', '4pm'],
    bestDays: ['Thursday', 'Friday', 'Saturday'],
    formats: ['long-form', 'shorts', 'live'],
    cadence: '2-3 videos/week'
  },
  tiktok: {
    icon: FaTiktok,
    label: 'TikTok',
    color: '#000000',
    maxLength: 2200,
    optimalTimes: ['7am', '12pm', '7pm'],
    bestDays: ['Tuesday', 'Thursday', 'Friday'],
    formats: ['short-video', 'duet', 'stitch', 'live'],
    cadence: '1-3 posts/day'
  },
  website: {
    icon: () => <FiFileText />,
    label: 'Blog/Website',
    color: '#10B981',
    maxLength: null,
    optimalTimes: ['10am'],
    bestDays: ['Tuesday', 'Wednesday'],
    formats: ['article', 'case-study', 'guide', 'news'],
    cadence: '2-4 posts/week'
  },
};

const CAMPAIGN_TYPES = [
  {
    id: 'awareness',
    label: 'Brand Awareness',
    icon: FiUsers,
    description: 'Increase visibility and reach new audiences',
    platforms: ['instagram', 'tiktok', 'youtube'],
    contentTypes: ['Behind-the-scenes', 'Brand story', 'Team spotlights', 'Values showcase'],
    duration: '4-6 weeks',
    kpis: ['Impressions', 'Reach', 'Followers']
  },
  {
    id: 'engagement',
    label: 'Community Engagement',
    icon: FiMessageSquare,
    description: 'Build relationships and foster conversations',
    platforms: ['twitter', 'linkedin', 'instagram'],
    contentTypes: ['Polls', 'Questions', 'User-generated', 'Discussions'],
    duration: 'Ongoing',
    kpis: ['Comments', 'Shares', 'Mentions']
  },
  {
    id: 'thought_leadership',
    label: 'Thought Leadership',
    icon: FiStar,
    description: 'Establish authority and expertise',
    platforms: ['linkedin', 'twitter', 'website'],
    contentTypes: ['Industry insights', 'Hot takes', 'Predictions', 'Research'],
    duration: 'Ongoing',
    kpis: ['Saves', 'Shares', 'Backlinks']
  },
  {
    id: 'lead_gen',
    label: 'Lead Generation',
    icon: FiTarget,
    description: 'Drive conversions and capture leads',
    platforms: ['linkedin', 'website', 'youtube'],
    contentTypes: ['Webinars', 'Ebooks', 'Case studies', 'Demos'],
    duration: '2-4 weeks',
    kpis: ['Clicks', 'Signups', 'Downloads']
  },
  {
    id: 'product_launch',
    label: 'Product Launch',
    icon: FiZap,
    description: 'Build hype and drive adoption for new releases',
    platforms: ['twitter', 'instagram', 'youtube', 'tiktok'],
    contentTypes: ['Teasers', 'Countdowns', 'Feature reveals', 'Tutorials'],
    duration: '2-3 weeks',
    kpis: ['Engagement rate', 'Click-throughs', 'Conversions']
  },
  {
    id: 'education',
    label: 'Educational Series',
    icon: FiLayers,
    description: 'Teach and provide value to your audience',
    platforms: ['youtube', 'linkedin', 'website', 'instagram'],
    contentTypes: ['How-tos', 'Tips', 'Tutorials', 'Explainers'],
    duration: '4-8 weeks',
    kpis: ['Watch time', 'Saves', 'Completion rate']
  },
];

const CONTENT_FORMATS = [
  { id: 'video', label: 'Video', icon: FiVideo, color: '#EF4444' },
  { id: 'image', label: 'Image/Graphic', icon: FiImage, color: '#8B5CF6' },
  { id: 'carousel', label: 'Carousel', icon: FiGrid, color: '#F59E0B' },
  { id: 'text', label: 'Text Only', icon: FiFileText, color: '#3B82F6' },
  { id: 'poll', label: 'Poll/Interactive', icon: FiBarChart2, color: '#10B981' },
  { id: 'live', label: 'Live Stream', icon: FiZap, color: '#EC4899' },
];

const ContentStrategy = ({ brandId, strategy: initialStrategy, voiceDNA, samples = [], onRefresh }) => {
  const [activeSection, setActiveSection] = useState('platforms');
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedStrategy, setGeneratedStrategy] = useState(initialStrategy?.generated || null);

  // Analyze which platforms have content
  const platformAnalysis = useMemo(() => {
    const analysis = {};

    Object.keys(PLATFORM_CONFIG).forEach(platform => {
      const platformSamples = samples.filter(s => s.platform === platform);
      const config = PLATFORM_CONFIG[platform];

      if (platformSamples.length > 0) {
        // Calculate avg word count
        const avgWords = Math.round(
          platformSamples.reduce((acc, s) => acc + (s.word_count || 0), 0) / platformSamples.length
        );

        // Calculate emoji usage
        const emojiCount = platformSamples.reduce((acc, s) => {
          const matches = (s.content || '').match(/[\u{1F300}-\u{1F9FF}]/gu);
          return acc + (matches ? matches.length : 0);
        }, 0);
        const emojiRate = (emojiCount / platformSamples.length).toFixed(1);

        // Calculate hashtag usage
        const hashtagCount = platformSamples.reduce((acc, s) => {
          const matches = (s.content || '').match(/#\w+/g);
          return acc + (matches ? matches.length : 0);
        }, 0);
        const hashtagRate = (hashtagCount / platformSamples.length).toFixed(1);

        // Detect content types
        const contentTypes = new Set();
        platformSamples.forEach(s => {
          if (s.content_type) contentTypes.add(s.content_type);
        });

        analysis[platform] = {
          ...config,
          sampleCount: platformSamples.length,
          avgWords,
          emojiRate,
          hashtagRate,
          contentTypes: Array.from(contentTypes),
          hasEnoughData: platformSamples.length >= 5
        };
      }
    });

    return analysis;
  }, [samples]);

  // Derive content pillars from samples
  const contentPillars = useMemo(() => {
    if (samples.length < 5) return [];

    // Group by content type and tags
    const themes = {};
    samples.forEach(sample => {
      const type = sample.content_type || 'general';
      if (!themes[type]) {
        themes[type] = { count: 0, samples: [] };
      }
      themes[type].count++;
      themes[type].samples.push(sample);
    });

    // Sort by frequency and take top 5
    return Object.entries(themes)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([type, data]) => ({
        name: type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        count: data.count,
        percentage: Math.round((data.count / samples.length) * 100)
      }));
  }, [samples]);

  // Generate AI strategy
  const generateStrategy = async () => {
    if (!brandId) return;

    setIsGenerating(true);
    try {
      const response = await contentTrainingService.generateContentStrategy(brandId);
      if (response.data.success) {
        setGeneratedStrategy(response.data.data);
        successToast('Content strategy generated');
      }
    } catch (error) {
      console.error('Strategy generation error:', error);
      errorToast('Failed to generate strategy');
    } finally {
      setIsGenerating(false);
    }
  };

  // Get voice adaptation for platform
  const getVoiceAdaptation = (platform) => {
    const baseVoice = voiceDNA?.spectrum || {};
    const adaptations = {
      twitter: {
        tone: 'More casual, punchy, direct',
        tips: ['Keep under 280 chars', 'Use threads for longer content', 'Engage with trending topics'],
        avoid: ['Long paragraphs', 'Too many hashtags', 'Overly formal language']
      },
      linkedin: {
        tone: 'Professional but personable',
        tips: ['Use line breaks for readability', 'Start with a hook', 'Add relevant hashtags at end'],
        avoid: ['Clickbait', 'Too casual/slang', 'Irrelevant content']
      },
      instagram: {
        tone: 'Visual-first, engaging, relatable',
        tips: ['Front-load the caption', 'Use emojis strategically', 'Include CTA'],
        avoid: ['Walls of text', 'Ignoring stories', 'Over-editing']
      },
      youtube: {
        tone: 'Educational, conversational, valuable',
        tips: ['Optimize titles for search', 'Use chapters', 'Strong hooks in first 30s'],
        avoid: ['Clickbait thumbnails', 'Long intros', 'Poor audio']
      },
      tiktok: {
        tone: 'Authentic, trend-aware, fast-paced',
        tips: ['Hook in first 3 seconds', 'Use trending sounds', 'Be authentic'],
        avoid: ['Over-produced content', 'Ignoring trends', 'Hard selling']
      },
      website: {
        tone: 'Thorough, SEO-optimized, valuable',
        tips: ['Use headers and structure', 'Include internal links', 'Optimize for keywords'],
        avoid: ['Thin content', 'Keyword stuffing', 'Walls of text']
      }
    };

    return adaptations[platform] || adaptations.twitter;
  };

  const activePlatforms = Object.keys(platformAnalysis);

  const renderPlatformStrategy = (platform, data) => {
    const Icon = data.icon;
    const isExpanded = expandedPlatform === platform;
    const adaptation = getVoiceAdaptation(platform);

    return (
      <div key={platform} className={`${Styles.platformCard} ${isExpanded ? Styles.expanded : ''}`}>
        <div
          className={Styles.platformHeader}
          onClick={() => setExpandedPlatform(isExpanded ? null : platform)}
        >
          <div className={Styles.platformIcon} style={{ backgroundColor: `${data.color}15`, color: data.color }}>
            <Icon />
          </div>
          <div className={Styles.platformInfo}>
            <h4>{data.label}</h4>
            <span className={Styles.platformMeta}>
              {data.sampleCount} samples analyzed • {data.hasEnoughData ? 'Strategy ready' : 'Need more data'}
            </span>
          </div>
          <div className={Styles.platformCadence}>
            <FiClock />
            <span>{data.cadence}</span>
          </div>
          <div className={Styles.expandIcon}>
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          </div>
        </div>

        {isExpanded && (
          <div className={Styles.platformDetails}>
            {/* Stats Row */}
            <div className={Styles.statsRow}>
              <div className={Styles.statBox}>
                <span className={Styles.statValue}>{data.avgWords}</span>
                <span className={Styles.statLabel}>Avg Words</span>
              </div>
              <div className={Styles.statBox}>
                <span className={Styles.statValue}>{data.emojiRate}</span>
                <span className={Styles.statLabel}>Emojis/Post</span>
              </div>
              <div className={Styles.statBox}>
                <span className={Styles.statValue}>{data.hashtagRate}</span>
                <span className={Styles.statLabel}>Hashtags/Post</span>
              </div>
              <div className={Styles.statBox}>
                <span className={Styles.statValue}>{data.maxLength || '∞'}</span>
                <span className={Styles.statLabel}>Max Length</span>
              </div>
            </div>

            {/* Optimal Times */}
            <div className={Styles.detailSection}>
              <h5><FiClock /> Optimal Posting Times</h5>
              <div className={Styles.timeSlots}>
                {data.optimalTimes.map((time, idx) => (
                  <span key={idx} className={Styles.timeSlot}>{time}</span>
                ))}
              </div>
              <p className={Styles.detailNote}>Best days: {data.bestDays.join(', ')}</p>
            </div>

            {/* Content Formats */}
            <div className={Styles.detailSection}>
              <h5><FiGrid /> Recommended Formats</h5>
              <div className={Styles.formatTags}>
                {data.formats.map((format, idx) => (
                  <span key={idx} className={Styles.formatTag}>{format}</span>
                ))}
              </div>
            </div>

            {/* Voice Adaptation */}
            <div className={Styles.detailSection}>
              <h5><FiMessageSquare /> Voice Adaptation</h5>
              <div className={Styles.adaptationBox}>
                <div className={Styles.adaptationTone}>
                  <strong>Tone:</strong> {adaptation.tone}
                </div>
                <div className={Styles.adaptationTips}>
                  <div className={Styles.tipColumn}>
                    <span className={Styles.tipLabel}>Do's</span>
                    {adaptation.tips.map((tip, idx) => (
                      <div key={idx} className={Styles.tipItem}>
                        <FiCheck className={Styles.tipIconGreen} />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                  <div className={Styles.tipColumn}>
                    <span className={Styles.tipLabel}>Don'ts</span>
                    {adaptation.avoid.map((item, idx) => (
                      <div key={idx} className={Styles.tipItem}>
                        <span className={Styles.tipIconRed}>×</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCampaignType = (campaign) => {
    const Icon = campaign.icon;
    const isExpanded = expandedCampaign === campaign.id;

    return (
      <div key={campaign.id} className={`${Styles.campaignCard} ${isExpanded ? Styles.expanded : ''}`}>
        <div
          className={Styles.campaignHeader}
          onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
        >
          <div className={Styles.campaignIcon}>
            <Icon />
          </div>
          <div className={Styles.campaignInfo}>
            <h4>{campaign.label}</h4>
            <p>{campaign.description}</p>
          </div>
          <div className={Styles.expandIcon}>
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          </div>
        </div>

        {isExpanded && (
          <div className={Styles.campaignDetails}>
            <div className={Styles.campaignGrid}>
              <div className={Styles.campaignSection}>
                <h5>Best Platforms</h5>
                <div className={Styles.platformPills}>
                  {campaign.platforms.map(p => {
                    const config = PLATFORM_CONFIG[p];
                    const PIcon = config?.icon || FiFileText;
                    return (
                      <span key={p} className={Styles.platformPill} style={{ borderColor: config?.color }}>
                        <PIcon style={{ color: config?.color }} />
                        {config?.label || p}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className={Styles.campaignSection}>
                <h5>Content Types</h5>
                <div className={Styles.contentTypeTags}>
                  {campaign.contentTypes.map((type, idx) => (
                    <span key={idx} className={Styles.contentTypeTag}>{type}</span>
                  ))}
                </div>
              </div>

              <div className={Styles.campaignSection}>
                <h5>Duration</h5>
                <span className={Styles.durationBadge}>{campaign.duration}</span>
              </div>

              <div className={Styles.campaignSection}>
                <h5>Key Metrics</h5>
                <div className={Styles.kpiTags}>
                  {campaign.kpis.map((kpi, idx) => (
                    <span key={idx} className={Styles.kpiTag}>{kpi}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.headerText}>
          <h3>Content Strategy</h3>
          <p>Platform-specific strategies and campaign playbooks based on your voice training</p>
        </div>
        <button
          className={Styles.generateBtn}
          onClick={generateStrategy}
          disabled={isGenerating || samples.length < 5}
        >
          {isGenerating ? (
            <>
              <FiRefreshCw className={Styles.spinning} />
              Generating...
            </>
          ) : (
            <>
              <FiZap />
              Generate AI Strategy
            </>
          )}
        </button>
      </div>

      {/* Section Navigation */}
      <div className={Styles.sectionNav}>
        <button
          className={`${Styles.sectionTab} ${activeSection === 'platforms' ? Styles.active : ''}`}
          onClick={() => setActiveSection('platforms')}
        >
          <FiGrid />
          Platform Strategies
        </button>
        <button
          className={`${Styles.sectionTab} ${activeSection === 'campaigns' ? Styles.active : ''}`}
          onClick={() => setActiveSection('campaigns')}
        >
          <FiTarget />
          Campaign Playbooks
        </button>
        <button
          className={`${Styles.sectionTab} ${activeSection === 'pillars' ? Styles.active : ''}`}
          onClick={() => setActiveSection('pillars')}
        >
          <FiLayers />
          Content Pillars
        </button>
        <button
          className={`${Styles.sectionTab} ${activeSection === 'calendar' ? Styles.active : ''}`}
          onClick={() => setActiveSection('calendar')}
        >
          <FiCalendar />
          Content Calendar
        </button>
      </div>

      {/* Content */}
      <div className={Styles.content}>
        {/* Platform Strategies */}
        {activeSection === 'platforms' && (
          <div className={Styles.platformsSection}>
            {activePlatforms.length > 0 ? (
              <div className={Styles.platformList}>
                {activePlatforms.map(platform => renderPlatformStrategy(platform, platformAnalysis[platform]))}
              </div>
            ) : (
              <div className={Styles.emptyState}>
                <FiGrid className={Styles.emptyIcon} />
                <h4>No Platform Data Yet</h4>
                <p>Add content samples from your social platforms to get platform-specific strategies.</p>
              </div>
            )}

            {/* Inactive Platforms */}
            {activePlatforms.length > 0 && activePlatforms.length < Object.keys(PLATFORM_CONFIG).length && (
              <div className={Styles.inactivePlatforms}>
                <h4>Expand Your Reach</h4>
                <p>Add content from these platforms to get tailored strategies:</p>
                <div className={Styles.inactivePlatformList}>
                  {Object.entries(PLATFORM_CONFIG)
                    .filter(([p]) => !activePlatforms.includes(p))
                    .map(([platform, config]) => {
                      const Icon = config.icon;
                      return (
                        <div key={platform} className={Styles.inactivePlatformCard}>
                          <Icon style={{ color: config.color }} />
                          <span>{config.label}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Campaign Playbooks */}
        {activeSection === 'campaigns' && (
          <div className={Styles.campaignsSection}>
            <div className={Styles.campaignIntro}>
              <FiInfo />
              <p>Select a campaign type to see platform recommendations, content ideas, and success metrics.</p>
            </div>
            <div className={Styles.campaignList}>
              {CAMPAIGN_TYPES.map(campaign => renderCampaignType(campaign))}
            </div>
          </div>
        )}

        {/* Content Pillars */}
        {activeSection === 'pillars' && (
          <div className={Styles.pillarsSection}>
            {contentPillars.length > 0 ? (
              <>
                <div className={Styles.pillarsIntro}>
                  <h4>Your Content Pillars</h4>
                  <p>Based on analysis of your {samples.length} content samples, these are your primary content themes:</p>
                </div>

                <div className={Styles.pillarsList}>
                  {contentPillars.map((pillar, idx) => (
                    <div key={idx} className={Styles.pillarCard}>
                      <div className={Styles.pillarRank}>#{idx + 1}</div>
                      <div className={Styles.pillarInfo}>
                        <h5>{pillar.name}</h5>
                        <span>{pillar.count} pieces ({pillar.percentage}%)</span>
                      </div>
                      <div className={Styles.pillarBar}>
                        <div
                          className={Styles.pillarFill}
                          style={{ width: `${pillar.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className={Styles.pillarRecommendation}>
                  <h4>Recommended Content Mix</h4>
                  <div className={Styles.mixGrid}>
                    {CONTENT_FORMATS.map(format => (
                      <div key={format.id} className={Styles.mixCard}>
                        <div className={Styles.mixIcon} style={{ color: format.color }}>
                          <format.icon />
                        </div>
                        <span className={Styles.mixLabel}>{format.label}</span>
                        <span className={Styles.mixPercent}>
                          {format.id === 'video' ? '30%' :
                           format.id === 'image' ? '25%' :
                           format.id === 'carousel' ? '20%' :
                           format.id === 'text' ? '15%' : '10%'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className={Styles.emptyState}>
                <FiLayers className={Styles.emptyIcon} />
                <h4>Not Enough Data</h4>
                <p>Add at least 5 content samples to identify your content pillars.</p>
              </div>
            )}
          </div>
        )}

        {/* Content Calendar */}
        {activeSection === 'calendar' && (
          <div className={Styles.calendarSection}>
            <div className={Styles.calendarIntro}>
              <h4>Weekly Content Calendar</h4>
              <p>Recommended posting schedule based on your platforms and audience</p>
            </div>

            <div className={Styles.calendarGrid}>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <div key={day} className={Styles.dayColumn}>
                  <div className={Styles.dayHeader}>{day.slice(0, 3)}</div>
                  <div className={Styles.daySlots}>
                    {activePlatforms.slice(0, 3).map(platform => {
                      const config = PLATFORM_CONFIG[platform];
                      const Icon = config.icon;
                      const shouldPost = config.bestDays.includes(day);

                      return shouldPost ? (
                        <div key={platform} className={Styles.calendarSlot} style={{ borderLeftColor: config.color }}>
                          <Icon style={{ color: config.color }} />
                          <span>{config.label}</span>
                          <span className={Styles.slotTime}>{config.optimalTimes[0]}</span>
                        </div>
                      ) : null;
                    })}
                    {activePlatforms.length === 0 && (
                      <div className={Styles.emptySlot}>-</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className={Styles.calendarSummary}>
              <h5>Weekly Summary</h5>
              <div className={Styles.summaryGrid}>
                {activePlatforms.map(platform => {
                  const config = PLATFORM_CONFIG[platform];
                  const Icon = config.icon;
                  return (
                    <div key={platform} className={Styles.summaryItem}>
                      <Icon style={{ color: config.color }} />
                      <span>{config.label}</span>
                      <strong>{config.cadence}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentStrategy;
